/**
 * SAM-624 — readable EN/ES messages for the PricingError codes of functions/booking/pricing.py.
 *
 * The API answers a rejected booking with `{ statusCode: 400, message, error_code }`. The server
 * `message` is English and carries internal ids, so the admin maps the stable `error_code` to copy
 * an operator can act on. Unknown codes return `null` and the caller keeps the server message.
 */

export type PricingErrorCode =
  | 'unknown_rate_group'
  | 'rate_group_not_eligible'
  | 'rate_group_not_sold'
  | 'rate_group_required'
  | 'rate_group_cap_per_booking'
  | 'rate_group_cap_per_day'
  | 'resource_required'
  | 'resource_not_found'
  | 'unit_max_pax_exceeded'
  | 'unit_price_missing'
  | 'invalid_unit_config'
  | 'event_over_capacity'
  | 'event_size_price_missing'
  | 'amount_mismatch'
  // SAM-626 — service save (CRUD 400): a season is malformed or two seasons share a day.
  | 'season_overlap'
  | 'invalid_season';

export interface PricingErrorDetails {
  max?: number;
  max_pax?: number;
  requested?: number;
  already_booked_today?: number;
  event_size?: string;
  /** SAM-626 — season ids (or already-resolved labels) named by the CRUD save error. */
  seasons?: Array<string | null>;
  season?: string | null;
  [key: string]: unknown;
}

export interface BilingualMessage {
  en: string;
  es: string;
}

const has = (value: unknown): value is number => typeof value === 'number';
// The 400 body ships `error_code` + `message` only, so numbers are optional: the parenthetical is
// added when a caller has the details (e.g. the preview), and dropped otherwise.
const maxReq = (en: boolean, max: unknown, requested: unknown): string =>
  has(max) ? ` (${en ? 'maximum' : 'máximo'} ${max}${has(requested) ? `, ${en ? 'requested' : 'solicitados'} ${requested}` : ''})` : '';

const MESSAGES: Record<PricingErrorCode, (d: PricingErrorDetails) => BilingualMessage> = {
  unknown_rate_group: () => ({
    en: 'That rate group does not exist for this service. Reload the service and pick a rate group again.',
    es: 'Ese grupo de tarifa no existe en este servicio. Recargue el servicio y elija el grupo de tarifa otra vez.',
  }),
  rate_group_not_eligible: () => ({
    en: 'This guest is not eligible for the selected rate group. Choose another rate group.',
    es: 'El huésped no es elegible para el grupo de tarifa elegido. Elija otro grupo de tarifa.',
  }),
  rate_group_not_sold: () => ({
    en: 'This ticket type is not sold in the selected rate group. Choose another rate group.',
    es: 'Este tipo de boleto no se vende en el grupo de tarifa elegido. Elija otro grupo de tarifa.',
  }),
  rate_group_required: () => ({
    en: 'Choose a rate group for every ticket type in the booking.',
    es: 'Elija un grupo de tarifa para cada tipo de boleto de la reserva.',
  }),
  rate_group_cap_per_booking: (d) => ({
    en: `Too many tickets in one rate group for a single booking${maxReq(true, d.max, d.requested)}.`,
    es: `Demasiados boletos en un mismo grupo de tarifa para una sola reserva${maxReq(false, d.max, d.requested)}.`,
  }),
  rate_group_cap_per_day: (d) => ({
    en: `The daily limit for this rate group is reached${has(d.max) ? ` (maximum ${d.max} per day)` : ''}.`,
    es: `Se alcanzó el límite diario de este grupo de tarifa${has(d.max) ? ` (máximo ${d.max} por día)` : ''}.`,
  }),
  resource_required: () => ({
    en: 'Choose the cabana or unit to book; this service has several and the price depends on which one.',
    es: 'Elija la cabaña o unidad a reservar; este servicio tiene varias y el precio depende de cuál sea.',
  }),
  resource_not_found: () => ({
    en: 'The selected cabana or unit no longer exists on this service. Pick another one.',
    es: 'La cabaña o unidad elegida ya no existe en este servicio. Elija otra.',
  }),
  unit_max_pax_exceeded: (d) => ({
    en: `Too many people for this unit${maxReq(true, d.max_pax ?? d.max, d.requested)}.`,
    es: `Demasiadas personas para esta unidad${maxReq(false, d.max_pax ?? d.max, d.requested)}.`,
  }),
  unit_price_missing: () => ({
    en: 'This unit has no price configured. Set it in the service pricing before booking.',
    es: 'Esta unidad no tiene precio configurado. Defínalo en los precios del servicio antes de reservar.',
  }),
  invalid_unit_config: () => ({
    en: 'The unit pricing of this service is misconfigured (price, included people or maximum people). Fix it in the service editor.',
    es: 'El precio por unidad de este servicio está mal configurado (precio, personas incluidas o máximo de personas). Corríjalo en el editor del servicio.',
  }),
  event_over_capacity: (d) => ({
    en: `The event is larger than the venue capacity${maxReq(true, d.max, d.requested)}.`,
    es: `El evento supera la capacidad del lugar${maxReq(false, d.max, d.requested)}.`,
  }),
  event_size_price_missing: (d) => ({
    en: `No price is set for this event size${d.event_size ? ` (${String(d.event_size).toUpperCase()})` : ''}. Set it in the service pricing.`,
    es: `No hay precio definido para este tamaño de evento${d.event_size ? ` (${String(d.event_size).toUpperCase()})` : ''}. Defínalo en los precios del servicio.`,
  }),
  amount_mismatch: () => ({
    en: 'The total changed since it was shown (prices were updated). Reload the service and try again.',
    es: 'El total cambió desde que se mostró (se actualizaron los precios). Recargue el servicio e intente de nuevo.',
  }),
  season_overlap: (d) => {
    const [a, b] = (d.seasons || []).map((s) => (s ? `"${s}"` : ''));
    const pair = a && b ? { en: `${a} and ${b}`, es: `${a} y ${b}` } : null;
    return {
      en: `${pair ? `The seasons ${pair.en} share` : 'Two seasons share'} at least one day (in some year). Change their dates so they do not overlap, then save again.`,
      es: `${pair ? `Las temporadas ${pair.es} comparten` : 'Dos temporadas comparten'} al menos un día (en algún año). Cambie sus fechas para que no se solapen y guarde de nuevo.`,
    };
  },
  invalid_season: (d) => ({
    en: `${d.season ? `The season "${d.season}" has` : 'A season has'} invalid dates. Check its start and end, then save again.`,
    es: `${d.season ? `La temporada "${d.season}" tiene` : 'Una temporada tiene'} fechas inválidas. Revise su inicio y fin y guarde de nuevo.`,
  }),
};

export function isPricingErrorCode(code: unknown): code is PricingErrorCode {
  return typeof code === 'string' && Object.prototype.hasOwnProperty.call(MESSAGES, code);
}

/** `{en, es}` for a known code, else `null` (caller falls back to the server message). */
export function pricingErrorMessage(code: unknown, details: PricingErrorDetails = {}): BilingualMessage | null {
  return isPricingErrorCode(code) ? MESSAGES[code](details) : null;
}

/** One-line text for toasts and inline errors: "English / Español". */
export function formatPricingError(code: unknown, details: PricingErrorDetails = {}): string | null {
  const msg = pricingErrorMessage(code, details);
  return msg ? `${msg.en} / ${msg.es}` : null;
}

const isSeasonCode = (code: unknown): code is 'season_overlap' | 'invalid_season' =>
  code === 'season_overlap' || code === 'invalid_season';

export interface SeasonSaveIssue {
  code: 'season_overlap' | 'invalid_season';
  /** Season ids the server named (`details.seasons`, or `[details.season]`). */
  ids: string[];
}

/**
 * SAM-626 — the season issue carried by a rejected service save, or null when the error is
 * something else. The CRUD 400 body is `{ error, code, details: { seasons: [idA, idB] } }`
 * (`details: { season }` for `invalid_season`); Axios keeps it at `error.response.data`.
 */
export function seasonSaveIssue(error: unknown): SeasonSaveIssue | null {
  const data = (error as { response?: { data?: any } } | null)?.response?.data;
  const code = data?.code ?? data?.error_code;
  if (!isSeasonCode(code)) return null;
  const details = data?.details || {};
  const ids: unknown[] = Array.isArray(details.seasons) ? details.seasons : details.season ? [details.season] : [];
  return { code, ids: ids.filter((id): id is string => typeof id === 'string') };
}

/**
 * "English / Español" text for a season save issue, naming the seasons by label. `labelOf` maps a
 * season id to the label the admin sees (falls back to the id).
 */
export function formatSeasonSaveIssue(issue: SeasonSaveIssue, labelOf: (id: string) => string = (id) => id): string {
  const names = issue.ids.map(labelOf);
  const details: PricingErrorDetails = issue.code === 'season_overlap' ? { seasons: names } : { season: names[0] ?? null };
  const msg = pricingErrorMessage(issue.code, details) as BilingualMessage;
  return `${msg.en} / ${msg.es}`;
}
